import React, { useState, useEffect, useContext } from "react";
import "../../styles/Report.css";
import {
  Layout,
  theme,
  Modal,
  Card,
  Typography,
  Button,
  Popover,
  Checkbox,
  Space,
  Divider,
  Alert,
} from "antd";
import {
  PieChartOutlined,
  ArrowRightOutlined,
  BarChartOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import LoadingOverlay from "react-loading-overlay-ts";

import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import PaginatedReportEmbed from "../../components/PowerBI/PaginatedReportEmbed";

import { TourContext } from "../../context/TourContext";
import { AuthContext } from "../../context/AuthContext";

import { getReportsApi } from "../../services/PowerBI/PowerBIApis";
import { getReportAccessConfigApi } from "../../services/Report/ReportApis";

interface PowerBIReport {
  id: number;
  ReportName: string;
  ReportID: string;
  DatasetID: string;
  Section: string;
  isActive: number;
}

const { Content } = Layout;

const ReportsPage: React.FC = () => {
  const { setOpen, setPageLoaded } = useContext(TourContext);
  const { currentGroup } = useContext(AuthContext);

  const [isActive, setActive] = useState(true);
  const [powerBIModalOpen, setPowerBIModalOpen] = useState(false);
  const [powerBIReports, setPowerBIReports] = useState<PowerBIReport[]>([]);
  const [selectedPowerBIReport, setSelectedPowerBIReport] = useState<{ reportId: string; datasetId: string; name: string } | null>(null);
  const [reportsError, setReportsError] = useState<string | null>(null);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  setTimeout(() => {
    setActive(false);
  }, 500);

  const openPowerBIReport = (report: PowerBIReport) => {
    setSelectedPowerBIReport({ reportId: report.ReportID, datasetId: report.DatasetID, name: report.ReportName });
    setPowerBIModalOpen(true);
  };

  useEffect(() => {
    setPageLoaded(true);
    setOpen(true);
    setReportsError(null);
    getReportsApi()
      .then((res: any) => {
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : null;

        if (list) {
          setPowerBIReports(list);
          return;
        }

        const status = res?.status ?? res?.statusCode;
        const msg =
          res?.message ||
          res?.error ||
          (status
            ? `The reports service returned an error (${status}).`
            : "The reports service is currently unavailable.");
        setPowerBIReports([]);
        setReportsError(msg);
      })
      .catch((err: any) => {
        console.error(err);
        setPowerBIReports([]);
        setReportsError(
          err?.message ||
            "Unable to load reports right now. Please try again in a moment."
        );
      });
  }, [setPageLoaded, setOpen]);

  type ReportCardDef = {
    id: string;
    category: string;
    label: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    background: string;
    active?: boolean;
    onClick?: () => void;
  };

  const reportCatalog: ReportCardDef[] = [
    ...powerBIReports.map<ReportCardDef>((r, i) => {
      const palette = [
        { color: "#1677ff", background: "#e6f0ff" },
        { color: "#722ed1", background: "#f9f0ff" },
        { color: "#13c2c2", background: "#e6fffb" },
        { color: "#fa8c16", background: "#fff7e6" },
        { color: "#52c41a", background: "#f0fff0" },
        { color: "#eb2f96", background: "#fff0f6" },
        { color: "#faad14", background: "#fffbe6" },
        { color: "#2f54eb", background: "#f0f5ff" },
      ];
      const p = palette[i % palette.length];
      return {
        id: `pbi-${r.ReportID || r.id}`,
        category: r.Section || "Power BI",
        label: r.ReportName,
        description: "Power BI embedded report",
        icon: <BarChartOutlined style={{ fontSize: 22, color: p.color }} />,
        color: p.color,
        background: p.background,
        active: true,
        onClick: () => openPowerBIReport(r),
      };
    }),
  ];

  const [visibleCards, setVisibleCards] = useState<Record<string, boolean>>({});
  const isVisible = (id: string) => visibleCards[id] !== false;
  const [visibilityOpen, setVisibilityOpen] = useState(false);

  const toggleCard = (id: string, checked: boolean) =>
    setVisibleCards((prev) => ({ ...prev, [id]: checked }));

  const toggleCategory = (category: string, checked: boolean) =>
    setVisibleCards((prev) => {
      const next = { ...prev };
      reportCatalog
        .filter((c) => c.category === category)
        .forEach((c) => {
          next[c.id] = checked;
        });
      return next;
    });

  const [adminAllowed, setAdminAllowed] = useState<Set<string> | null>(null);
  const [configLoading, setConfigLoading] = useState(true);

  const fetchAdminAllowed = () => {
    if (!currentGroup) return;
    setConfigLoading(true);
    getReportAccessConfigApi(Number(currentGroup))
      .then((res: any) => {
        const cfg = res?.data?.Config ?? res?.data?.config;
        if (cfg && Array.isArray(cfg.enabledReports)) {
          setAdminAllowed(new Set(cfg.enabledReports));
        } else {
          setAdminAllowed(null);
        }
      })
      .catch(() => setAdminAllowed(null))
      .finally(() => setConfigLoading(false));
  };

  useEffect(() => { fetchAdminAllowed(); }, [currentGroup]); // eslint-disable-line

  useEffect(() => {
    const onUpdate = () => fetchAdminAllowed();
    window.addEventListener("adminAllowedReportsChanged", onUpdate);
    window.addEventListener("focus", onUpdate);
    let bc: BroadcastChannel | null = null;
    try { bc = new BroadcastChannel("reportAccessConfig"); bc.onmessage = onUpdate; } catch {}
    return () => {
      window.removeEventListener("adminAllowedReportsChanged", onUpdate);
      window.removeEventListener("focus", onUpdate);
      bc?.close();
    };
  }, [currentGroup]); // eslint-disable-line

  const visibleCatalog = configLoading
    ? []
    : reportCatalog.filter(
        (c) => c.active && (adminAllowed === null || adminAllowed.has(c.id))
      );

  const categories = Array.from(new Set(visibleCatalog.map((c) => c.category)));

  const visibilityContent = (
    <div style={{ width: 280, maxHeight: 420, overflowY: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() =>
            setVisibleCards(visibleCatalog.reduce((a, c) => ({ ...a, [c.id]: true }), {}))
          }
        >
          Select all
        </Button>
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() =>
            setVisibleCards(visibleCatalog.reduce((a, c) => ({ ...a, [c.id]: false }), {}))
          }
        >
          Clear all
        </Button>
      </div>
      {categories.map((cat) => {
        const items = visibleCatalog.filter((c) => c.category === cat);
        const allOn = items.every((c) => isVisible(c.id));
        const someOn = items.some((c) => isVisible(c.id));
        return (
          <div key={cat} style={{ marginBottom: 12 }}>
            <Checkbox
              checked={allOn}
              indeterminate={!allOn && someOn}
              onChange={(e) => toggleCategory(cat, e.target.checked)}
            >
              <strong>{cat}</strong>
            </Checkbox>
            <div style={{ paddingLeft: 24, marginTop: 4 }}>
              <Space direction="vertical" size={4}>
                {items.map((c) => (
                  <Checkbox
                    key={c.id}
                    checked={isVisible(c.id)}
                    onChange={(e) => toggleCard(c.id, e.target.checked)}
                  >
                    {c.label}
                  </Checkbox>
                ))}
              </Space>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <SideBar />
        <Layout className="site-layout">
          <TopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <h2 style={{ margin: 0 }}>
                  <PieChartOutlined
                    style={{ paddingRight: "5px", color: "#f04f05" }}
                  />
                  Reports
                </h2>
                {!reportsError && (
                  <Popover
                    trigger="click"
                    placement="bottomRight"
                    open={visibilityOpen}
                    onOpenChange={setVisibilityOpen}
                    title="Show / hide report cards"
                    content={visibilityContent}
                  >
                    <Button type="primary" danger icon={<SettingOutlined />}>
                      Customize
                    </Button>
                  </Popover>
                )}
              </div>

              {reportsError && (
                <Alert
                  type="error"
                  showIcon
                  message="Unable to load reports"
                  style={{ marginTop: 16 }}
                  action={
                    <Button
                      size="small"
                      danger
                      onClick={() => {
                        setReportsError(null);
                        getReportsApi()
                          .then((res: any) => {
                            const list = Array.isArray(res)
                              ? res
                              : Array.isArray(res?.data)
                              ? res.data
                              : null;
                            if (list) {
                              setPowerBIReports(list);
                              return;
                            }
                            const status = res?.status ?? res?.statusCode;
                            setReportsError(
                              res?.message ||
                                res?.error ||
                                (status
                                  ? `The reports service returned an error (${status}).`
                                  : "The reports service is currently unavailable.")
                            );
                          })
                          .catch((err: any) => {
                            setReportsError(
                              err?.message ||
                                "Unable to load reports right now. Please try again in a moment."
                            );
                          });
                      }}
                    >
                      Retry
                    </Button>
                  }
                />
              )}

              {!reportsError && categories.map((cat, catIdx) => {
                const items = reportCatalog.filter(
                  (c) =>
                    c.category === cat &&
                    c.active &&
                    (adminAllowed === null || adminAllowed.has(c.id)) &&
                    isVisible(c.id)
                );
                if (items.length === 0) return null;
                const palette = [
                  { color: "#1677ff", background: "#e6f0ff" },
                  { color: "#722ed1", background: "#f9f0ff" },
                  { color: "#13c2c2", background: "#e6fffb" },
                  { color: "#fa8c16", background: "#fff7e6" },
                  { color: "#52c41a", background: "#f0fff0" },
                  { color: "#eb2f96", background: "#fff0f6" },
                  { color: "#faad14", background: "#fffbe6" },
                  { color: "#2f54eb", background: "#f0f5ff" },
                ];
                const p = palette[catIdx % palette.length];
                return (
                  <div key={cat} style={{ marginTop: 28 }}>
                    <Divider
                      orientation="left"
                      style={{ borderColor: p.color }}
                    >
                      <Typography.Text
                        style={{ color: p.color, fontWeight: 600 }}
                      >
                        <BarChartOutlined style={{ marginRight: 8 }} />
                        {cat}
                      </Typography.Text>
                    </Divider>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(230px, 1fr))",
                        gap: 16,
                        marginTop: 12,
                      }}
                    >
                      {items.map((c) => (
                        <Card
                          key={c.id}
                          hoverable={!!c.active}
                          onClick={c.active ? c.onClick : undefined}
                          style={{
                            borderRadius: 12,
                            borderTop: `6px solid ${p.color}`,
                            cursor: c.active ? "pointer" : "default",
                            opacity: c.active ? 1 : 0.85,
                          }}
                          bodyStyle={{ padding: "14px 16px" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                background: p.background,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {React.cloneElement(c.icon as React.ReactElement, {
                                style: { fontSize: 22, color: p.color },
                              })}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Typography.Text
                                style={{
                                  fontSize: 11,
                                  color: c.active ? p.color : undefined,
                                  fontWeight: 600,
                                }}
                                type={c.active ? undefined : "secondary"}
                              >
                                {c.active ? "Generate" : "Coming soon"}
                              </Typography.Text>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: 14,
                                  marginTop: 2,
                                  wordBreak: "break-word",
                                }}
                              >
                                {c.label}
                              </div>
                              <Typography.Text
                                type="secondary"
                                style={{
                                  fontSize: 11,
                                  display: "block",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {c.description}
                              </Typography.Text>
                            </div>
                            {c.active && (
                              <ArrowRightOutlined style={{ color: p.color }} />
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}

            </div>

            {/* Power BI Paginated Report Modal */}
            <Modal
              open={powerBIModalOpen}
              onCancel={() => setPowerBIModalOpen(false)}
              footer={null}
              width="90%"
              closable={false}
              bodyStyle={{ padding: 0 }}
              style={{ top: 20 }}
              className="powerbi-report-modal"
              destroyOnClose
            >
              <div
                style={{
                  background: "linear-gradient(135deg, #722ed1 0%, #531dab 100%)",
                  borderRadius: "8px 8px 0 0",
                  padding: "20px 24px",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <BarChartOutlined style={{ fontSize: 20, color: "#fff" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700 }}>{selectedPowerBIReport?.name ?? "Power BI Report"}</div>
                    <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>Power BI Paginated Report</div>
                  </div>
                </div>
                <Button
                  type="text"
                  onClick={() => setPowerBIModalOpen(false)}
                  style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}
                >
                  ✕
                </Button>
              </div>
              <PaginatedReportEmbed
                visible={powerBIModalOpen}
                reportId={selectedPowerBIReport?.reportId}
                datasetId={selectedPowerBIReport?.datasetId}
                groupId={currentGroup ?? undefined}
              />
            </Modal>
          </Content>
        </Layout>
      </Layout>
    </LoadingOverlay>
  );
};

export default ReportsPage;
